import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { useApp } from '@/hooks/use-app-context';
import { useToast } from '@/hooks/use-toast';
import { useLanguages } from '@/hooks/use-languages';
import { getDefaultLanguages } from '@/lib/language.utils';
import { safePlay } from '@/lib/audio-playback';
import { phraseTranslationPayload, transcriptionFrenchApi } from '@/api/transcriptionFrench.api';
import { wordApi } from '@/api/word.api';
import { useSessionDeepLink } from '@/hooks/use-session-deeplink';
import { rememberCollectorModuleContribution, resolveCollectorContributionContext } from '@/lib/community-collector.storage';
import { useAudioWaveform } from '@/features/contribution-kit';
import { useValidationGate, isCrossValidationBlockedError } from '@/features/validation-gate';
import { isNetworkError, isOfflineQueueError, submitOrQueue } from '@/features/offline';
import type { ValidationGate } from '@/types/auth.types';
import type { PhraseEntry, SpeakContributeData } from './types';
import { normalizeDatasetEntries, buildStorageKey, loadProgress, saveProgress } from './helpers';
import { createShuffleSession, rememberShufflePage } from './shuffle-session';
import { offlineTranslateBatch, readTranslateReserve, topUpTranslateReserve } from './translate-offline-reserve';
import {
  TRANSCRIPTION_PHRASE_PAGE_SIZE,
  collectorContribution,
  firstOpenIndex,
  speakContributePhrase,
  stepAfterSkip,
  stepAfterSubmit,
  translationSubmission,
  translationSubmitBlocker,
} from './transcription-phrase.session';

/**
 * State and actions of a "Traduire" session: shuffled pages of phrases,
 * progress kept in localStorage, submit / skip / previous, validation gate,
 * collector context, the `?session=transcription` deep link and the single
 * phrase opened from "Enregistrer" (router state).
 */
export function useTranscriptionSession(fromSpeakContributeData: SpeakContributeData | null | undefined, locationState: unknown) {
  const { t, user, language, theme } = useApp();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const { activeLanguages, isLoading: isLoadingLanguages } = useLanguages();
  const collectorContext = useMemo(() => resolveCollectorContributionContext(locationState), [locationState]);
  const validationGate = useValidationGate();
  const [blockedGate, setBlockedGate] = useState<ValidationGate | null>(null);
  const clearBlockedGate = useCallback(() => setBlockedGate(null), []);

  const languages = activeLanguages.length > 0 ? activeLanguages : getDefaultLanguages();
  const targetLanguages = languages.filter((l) => l.code !== 'fr');

  const [selectedLanguage, setSelectedLanguage] = useState('');
  const [sessionStarted, setSessionStarted] = useState(false);
  const [sessionComplete, setSessionComplete] = useState(false);
  const [submittedCount, setSubmittedCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [phrases, setPhrases] = useState<PhraseEntry[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [translatedText, setTranslatedText] = useState('');
  const [showKeyboard, setShowKeyboard] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState<number | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [completedPhrases, setCompletedPhrases] = useState<Set<string>>(new Set());
  const [skippedPhrases, setSkippedPhrases] = useState<Set<string>>(new Set());
  const shuffleSessionRef = useRef<ReturnType<typeof createShuffleSession> | null>(null);
  if (!shuffleSessionRef.current) shuffleSessionRef.current = createShuffleSession();

  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  const currentLang = useMemo(() => {
    const param = window.location.pathname.split('/')[1];
    return param || language || 'fr';
  }, [language]);

  const storageKey = useMemo(() => buildStorageKey(user?.id, selectedLanguage), [user?.id, selectedLanguage]);

  const current = phrases[currentIndex] || null;
  const { canvasRef } = useAudioWaveform({ audioRef, audioUrl: current?.audioUrl, theme });

  useEffect(() => () => {
    queryClient.removeQueries({ queryKey: ['translation-dataset', user?.id, selectedLanguage] });
  }, [queryClient, user?.id, selectedLanguage]);

  // Load progress from localStorage
  useEffect(() => {
    if (!selectedLanguage || !user?.id || sessionStarted) return;
    queryClient.removeQueries({ queryKey: ['translation-dataset', user.id, selectedLanguage] });
    shuffleSessionRef.current = createShuffleSession();
    setSkippedPhrases(new Set());
    const saved = loadProgress(storageKey);
    setCompletedPhrases(new Set(saved ? saved.completedPhrases : []));
    setPage(1);
  }, [selectedLanguage, user?.id, sessionStarted, storageKey, queryClient]);

  // Save progress to localStorage
  useEffect(() => {
    if (!selectedLanguage || !user?.id || !sessionStarted) return;
    saveProgress(storageKey, Array.from(completedPhrases), page, currentIndex);
  }, [completedPhrases, page, currentIndex, selectedLanguage, user?.id, sessionStarted, storageKey]);

  // Handle deep link from SpeakContribute
  useEffect(() => {
    if (!fromSpeakContributeData?.fromSpeakContribute) return;
    if (fromSpeakContributeData.targetLanguage) {
      setSelectedLanguage(fromSpeakContributeData.targetLanguage);
    }
    const phrase = speakContributePhrase(fromSpeakContributeData, Date.now());
    if (phrase) {
      setPhrases([phrase]);
      setCurrentIndex(0);
      setPage(1);
      setSessionStarted(true);
      setTotal(1);
      setHasMore(false);
    }
  }, [fromSpeakContributeData]);

  const loadPage = useCallback(async (newPage: number, completedSet?: Set<string>) => {
    if (!selectedLanguage || !user?.id) return;
    setIsLoading(true);
    const currentCompleted = completedSet || completedPhrases;
    try {
      const session = shuffleSessionRef.current;
      if (!session) return;
      const response = await queryClient.fetchQuery({
        queryKey: ['translation-dataset', user.id, selectedLanguage, session.seed, newPage],
        staleTime: Infinity,
        gcTime: Infinity,
        // Offline, fail at once (and use the reserve) instead of waiting paused for the network.
        networkMode: 'always',
        queryFn: () => transcriptionFrenchApi.getTranslationSourceDataset(selectedLanguage, {
          page: newPage,
          limit: TRANSCRIPTION_PHRASE_PAGE_SIZE,
          excludeUserId: String(user.id),
          shuffleSeed: session.seed,
          cursor: session.cursors.get(newPage),
          datasetSource: session.source || undefined,
        }),
      });
      if (session !== shuffleSessionRef.current) return;

      const loadedPhrases = normalizeDatasetEntries(response, newPage).filter((phrase) => !currentCompleted.has(phrase.id));
      const responseTotal = typeof response?.total === 'number' ? response.total : loadedPhrases.length;

      setPhrases(loadedPhrases);
      setPage(newPage);
      setTranslatedText('');
      setTotal(responseTotal);
      setHasMore(rememberShufflePage(session, newPage, response));
      setCurrentIndex(firstOpenIndex(loadedPhrases, currentCompleted, skippedPhrases));

      if (!loadedPhrases.length) {
        toast({ title: t.noPhraseAvailable, description: t.retryLater });
      }
      // Keep phrases on the device, for when the network goes away.
      void topUpTranslateReserve(String(user.id), selectedLanguage, currentCompleted).catch((error) =>
        console.debug('Unable to refresh the offline phrase reserve', error),
      );
    } catch (err: unknown) {
      // No network: the next phrases kept on the device, neither translated nor skipped.
      if (isNetworkError(err)) {
        const reserve = await readTranslateReserve(String(user.id), selectedLanguage).catch((): PhraseEntry[] => []);
        const batch = offlineTranslateBatch(reserve, new Set([...currentCompleted, ...skippedPhrases]), TRANSCRIPTION_PHRASE_PAGE_SIZE);
        if (batch.phrases.length) {
          setPhrases(batch.phrases);
          setPage(newPage);
          setTranslatedText('');
          setTotal(null);
          setHasMore(batch.hasMore);
          setCurrentIndex(0);
          return;
        }
        toast({ title: t.translationLoadError, description: t.offline.reserveEmpty, variant: 'destructive' });
        return;
      }
      const message = err instanceof Error ? err.message : String(err);
      toast({ title: t.translationLoadError, description: message || t.phraseRetrieveError, variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  }, [selectedLanguage, user?.id, completedPhrases, skippedPhrases, toast, t, queryClient]);

  const startSession = useCallback(async () => {
    if (!selectedLanguage) {
      toast({ title: t.languageRequiredTitle, description: t.languageRequiredDesc, variant: 'destructive' });
      return;
    }
    if (!user?.id) {
      toast({ title: t.loginRequired, description: t.loginRequiredDesc, variant: 'destructive' });
      return;
    }
    if (fromSpeakContributeData?.fromSpeakContribute && fromSpeakContributeData.frenchPhrase) return;
    await loadPage(page >= 1 ? page : 1);
    setSessionStarted(true);
  }, [selectedLanguage, user?.id, fromSpeakContributeData, loadPage, page, toast, t]);

  const { startLinkedSession, sessionResumeActive } = useSessionDeepLink({
    searchParams,
    setSearchParams,
    sessionValue: 'transcription',
    selectedLanguage,
    setSelectedLanguage,
    sessionStarted,
    isStartingSession: isLoading,
    startSession,
    canResume: !fromSpeakContributeData?.fromSpeakContribute,
  });

  const playAudio = useCallback(() => {
    const element = audioRef.current;
    if (!element) return;
    if (isPlaying) {
      element.pause();
      setIsPlaying(false);
      return;
    }
    setIsPlaying(true);
    safePlay(element, { onError: () => setIsPlaying(false) });
  }, [isPlaying]);

  const onAudioEnded = useCallback(() => setIsPlaying(false), []);

  const selectPhrase = useCallback((index: number) => {
    setCurrentIndex(index);
    setTranslatedText('');
  }, []);

  const submitTranslation = useCallback(async () => {
    const blocker = translationSubmitBlocker({
      hasCurrent: Boolean(current),
      translatedText,
      isSubmitting,
      gateBlocked: validationGate.isBlocked,
      communityProfileId: collectorContext?.communityProfileId,
    });
    if (blocker === 'textRequired') {
      toast({ title: t.textRequired, description: t.textRequiredDesc, variant: 'destructive' });
      return;
    }
    if (blocker === 'busy' || !current) return;
    if (blocker === 'gate') {
      setBlockedGate(validationGate.gate ?? null);
      return;
    }

    setIsSubmitting(true);
    try {
      const submission = translationSubmission({ role: user?.role, language: selectedLanguage, phrase: current, translatedText, collector: collectorContext });
      let queued = false;
      if (submission.kind === 'direct') {
        // Applied at once by a moderator: online only, nothing is queued.
        try {
          await wordApi.updateTranslation(...submission.args);
        } catch (error) {
          if (isNetworkError(error)) {
            toast({ title: t.error, description: t.offline.directTranslationOnline, variant: 'destructive' });
            return;
          }
          throw error;
        }
      } else {
        const [lang, fr, local, opts] = submission.args;
        // Without network the proposal is kept on the device and sent later (OfflineSyncProvider).
        const outcome = await submitOrQueue({
          ownerId: String(user?.id ?? ''),
          kind: 'transcription-batch',
          payload: phraseTranslationPayload(lang, fr, local, opts),
          summary: { lang, text: fr },
          send: (headers) => transcriptionFrenchApi.submitPhraseTranslation(lang, fr, local, opts, { headers }),
        });
        queued = outcome.queued;
      }

      rememberCollectorModuleContribution(collectorContext, collectorContribution(current, translatedText));

      const newCompleted = new Set(completedPhrases);
      newCompleted.add(current.id);
      setCompletedPhrases(newCompleted);
      setSubmittedCount((count) => count + 1);
      if (queued) toast({ title: t.offline.savedTitle, description: t.offline.savedDesc });
      else toast({ title: t.translationAdded, description: t.translationSavedDesc });
      setTranslatedText('');

      const step = stepAfterSubmit({ phrases, currentIndex, completed: newCompleted, skipped: skippedPhrases, hasMore });
      if (step.type === 'goto') {
        setCurrentIndex(step.index);
      } else if (step.type === 'nextPage') {
        await loadPage(page + 1, newCompleted);
      } else {
        toast({ title: t.sessionCompleted, description: t.allPhrasesTranslated });
        setSessionComplete(true);
      }
    } catch (err: unknown) {
      const blockedFromError = isCrossValidationBlockedError(err);
      if (blockedFromError) {
        validationGate.setGateFromError(blockedFromError);
        setBlockedGate(blockedFromError);
        return;
      }
      if (isOfflineQueueError(err)) {
        toast({ title: t.error, description: t.offline.storageError, variant: 'destructive' });
        return;
      }
      const message = err instanceof Error ? err.message : String(err);
      toast({ title: t.error, description: message || t.submitFailed, variant: 'destructive' });
    } finally {
      setIsSubmitting(false);
    }
  }, [current, translatedText, selectedLanguage, completedPhrases, skippedPhrases, phrases, currentIndex, hasMore, page, collectorContext, toast, t, loadPage, isSubmitting, user?.id, user?.role, validationGate]);

  const previous = useCallback(() => {
    if (currentIndex > 0) setCurrentIndex(currentIndex - 1);
  }, [currentIndex]);

  const skipPhrase = useCallback(async () => {
    setTranslatedText('');
    if (current) {
      setSkippedPhrases((prev) => new Set(prev).add(current.id));
    }

    const step = stepAfterSkip({ phrases, currentIndex, currentId: current?.id, completed: completedPhrases, skipped: skippedPhrases, hasMore });
    if (step.type === 'goto') {
      setCurrentIndex(step.index);
      toast({ title: String(t.phraseSkipped ?? 'Phrase passée'), description: String(t.phraseSkippedDesc ?? 'Passé à la phrase suivante') });
    } else if (step.type === 'nextPage') {
      await loadPage(page + 1);
      toast({ title: String(t.phraseSkipped ?? 'Phrase passée'), description: String(t.loadingNewPhrases ?? 'Chargement de nouvelles phrases...') });
    } else {
      toast({ title: String(t.noMorePhrases ?? 'Plus de phrases'), description: String(t.noMorePhrasesDesc ?? 'Aucune autre phrase disponible pour le moment') });
    }
  }, [current, currentIndex, phrases, completedPhrases, skippedPhrases, hasMore, page, loadPage, toast, t]);

  const nextPage = useCallback(async () => {
    if (hasMore && !isLoading) await loadPage(page + 1);
  }, [hasMore, isLoading, loadPage, page]);

  const prevPage = useCallback(async () => {
    if (page > 1 && !isLoading) await loadPage(page - 1);
  }, [page, isLoading, loadPage]);

  const dismissSessionComplete = useCallback(() => setSessionComplete(false), []);

  return {
    selectedLanguage, setSelectedLanguage,
    sessionStarted, setSessionStarted,
    sessionComplete, dismissSessionComplete,
    submittedCount,
    sessionResumeActive,
    isLoading,
    isSubmitting,
    phrases, currentIndex, setCurrentIndex,
    translatedText, setTranslatedText,
    showKeyboard, setShowKeyboard,
    page, total, hasMore,
    completedPhrases,
    current,
    isPlaying,
    audioRef,
    canvasRef,
    targetLanguages,
    isLoadingLanguages,
    fromSpeakContributeData,
    currentLang,
    collectorContext,
    validationGate,
    blockedGate,
    clearBlockedGate,
    progressPercent: phrases.length > 0 ? Math.round((completedPhrases.size / phrases.length) * 100) : 0,
    startLinkedSession,
    loadPage,
    playAudio,
    onAudioEnded,
    selectPhrase,
    submitTranslation,
    skipPhrase,
    previous,
    nextPage,
    prevPage,
  };
}
